import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { NavController } from '@ionic/angular/standalone';
import { AuthenticationService } from './authentication.service';
import { PersonService } from './person.service';
import { ProjectCheckInService } from './project-check-in.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  #authService = inject(AuthenticationService);
  #personService = inject(PersonService);
  #navController = inject(NavController);
  #zone = inject(NgZone);
  #router = inject(Router);
  #projectCheckInService = inject(ProjectCheckInService);

  initListeners() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.#zone.run(() => this.handleAppUrlOpen(event.url));
    });

    App.addListener('appStateChange', ({ isActive }) => this.#zone.run(() => this.onAppStateChange(isActive)));
  }

  /**
   * A deeplink brought the app to the foreground: route to what it points at.
   *
   * Split out of the listener above so it can be tested - the Capacitor plugin is a Proxy a spy
   * cannot attach to, as `onAppStateChange` already had to work around.
   */
  handleAppUrlOpen(url: string): void {
    const parsed = new URL(url);
    const path = routePath(parsed.pathname);

    console.info('App URL Opened:', { url, path, search: parsed.search });

    // A person QR carries its payload as a valueless query string - `?V{n}.{id}.{expiresAt}.{signature}`
    // - so it cannot travel as an ordinary parameter: the router serialises `{'V2.…': ''}` back with a
    // trailing `=`, which corrupts the base-10 signature the backend verifies. Hand it over named.
    if (path === '/verify') {
      this.#router.navigate(['/verify'], { queryParams: { payload: parsed.search.slice(1) } });
      return;
    }

    this.#router.navigate([path], { queryParams: Object.fromEntries(parsed.searchParams.entries()) });
  }

  /**
   * A check-in ends on its own at the end of the working day (ILOPUMA-653), which the app cannot
   * notice while it sits idle: refreshes come from navigation (ionViewWillEnter) and pull-to-refresh
   * only. Resuming after that boundary would otherwise leave the home card claiming "checked in to
   * project X", with Check out answering 409.
   *
   * One refresh reaches every screen: `ProjectRequirementsCheckInService.checkInState` is a
   * root-level signal fed by `refreshCheckIn$`, which home reads `currentCheckIn()` off directly.
   */
  onAppStateChange(isActive: boolean): void {
    if (isActive) {
      this.#projectCheckInService.triggerRefresh();
    }
  }

  removeListeners() {
    App.removeAllListeners();
  }
}

/**
 * Angular matches route paths case-sensitively, while a person QR's base URL is uppercased
 * server-side and ends in a slash - so a scanned code would match no route at all.
 * `UnifiedQrScanService` lowercases scanned URLs for exactly this reason.
 */
function routePath(pathname: string): string {
  const lowered = pathname.toLowerCase();
  return lowered.length > 1 ? lowered.replace(/\/$/, '') : lowered;
}
